import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import update from "immutability-helper";
import { useQuery, useMutation } from "graphql-hooks";
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Divider,
  TextField,
  Radio,
  RadioGroup,
  Checkbox,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  makeStyles,
  createMuiTheme,
  ThemeProvider,
} from "@material-ui/core";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import IconButton from "@mui/material/IconButton";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import ShortTextIcon from "@mui/icons-material/ShortText";
import SubjectIcon from "@mui/icons-material/Subject";
import TocIcon from "@mui/icons-material/Toc";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { OrderObject } from "./OrderObject.js";
import { FormComponents as Components } from "./FormComponents.js";
import { ComponentDragBox } from "./ComponentDragBox.js";
import { FormDropBox } from "./FormDropBox.js";
import Header from "./Header.js";
import AlertDialog from "../../components/AlertDialog.js";
import EditTextToFormDialog from "./components/EditTextToFormDialog.js";
import EditTextAreaToFormDialog from "./components/EditTextAreaToFormDialog.js";
import EditContentToFormDialog from "./components/EditContentToFormDialog.js";
import EditRadioButtonToFormDialog from "./components/EditRadioButtonToFormDialog.js";
import EditDropDownToFormDialog from "./components/EditDropDownToFormDialog.js";
import EditCheckboxToFormDialog from "./components/EditCheckboxToFormDialog.js";
import backImg from "../../assets/icons/back.png";
import penImg from "../../assets/icons/pen.png";
import arrowupImg from "../../assets/icons/arrow-up-right.png";
import dropdownImg from "../../assets/icons/dropdown.png";
import infoImg from "../../assets/icons/info.png";
import trashImg from "../../assets/icons/trash.png";

import { CREATE_RADIOBUTTON_MUTATION } from "../../graphQL/radio_button__qry";
import { CREATE_TEXT_COMPONENT_MUTATION } from "../../graphQL/text_qry";
import { CREATE_DROPDOWN_MUTATION } from "../../graphQL/drop_down_qry";
import { CREATE_CHECKBOX_MUTATION } from "../../graphQL/checkbox_qry";

import { project_url } from "../../constants/Data";

const StyledTooltip = styled((props) => (
  <Tooltip
    classes={{ popper: props.className }}
    {...props}
    PopperProps={{
      popperOptions: {
        modifiers: {
          flip: { enabled: false },
          offset: {
            offset: "0px, -50px, 20px, -25px",
          },
        },
      },
    }}
  />
))`
  & .MuiTooltip-tooltip {
    display: flex;
    background: #757575;
    color: #fff;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
  }
`;

const formLabelsTheme = createMuiTheme({
  overrides: {
    MuiFormLabel: {
      asterisk: {
        color: "#DB3131",
        "&$error": {
          color: "#DB3131",
        },
      },
    },
  },
});

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    marginLeft: "8rem",
    marginRight: "8rem",
    marginTop: "2rem",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  container: {
    marginTop: "2rem",
    marginBottom: "5rem",
    overflow: "hidden",
    clear: "both",
    height: "936px",
    borderRadius: "8px",
  },

  ComponentDragBox: {
    float: "left",
  },
  FormDropBox: {
    float: "left",
    marginLeft: "5rem",
    marginTop: ".5rem",
  },
  outlinedButtonStyle: {
    borderRadius: 8,
    width: "110px",
    padding: 0,
    height: "48px",
    textTransform: "none",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    //  textTransform: "none",
    // "&:active": {
    //   backgroundColor: "#765285",
    // },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  dataGridStyle: {
    "& .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      fontSize: 12,
    },
  },
  textFieldStyle: {
    marginBottom: 5,
    width: 600,
    //marginTop: 20,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },
  formNameTextBoxStyle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#E2F2FF",
    },
  },
  objectBoxStyle: {
    margin: 15,
    borderRadius: "8px",
    border: "1px solid lightGray",
    "&:hover": {
      backgroundColor: "#E2F2FF",
    },
  },
  formNameTextStyle: {
    fontWeight: 500,
    fontSize: 24,
    display: "flex",
    padding: 5,
    alignItems: "center",
    lineHeight: 1,
  },
  iconStyle: {
    marginLeft: 12,
    height: 18,
    padding: 10,
    display: "flex",
    alignItems: "center",
    border: "1px solid lightGray",
    borderRadius: "4px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#EEEEEE",
    },
  },
  formNameFieldStyle: {
    width: 600,
    //marginTop: 20,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
      height: 40,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },
  dragIconBoxStyle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    paddingTop: 15,
    paddingBottom: 15,
    paddingLeft: 12,
    paddingRight: 2,
    marginTop: 5,
    marginLeft: 5,
    marginRight: 5,
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#E2F2FF",
    },
  },
  selectStyle: {
    maxWidth: 364,
    textAlign: "left",
  },
  radioLabelStyle: {
    "& .MuiTypography-body1": {
      fontSize: 16,
    },
  },
}));

function OrderFormDialog() {
  const { formId } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const [mutiOptFields, setMutiOptFields] = useState([]);
  const [alertMsg, setAlertMsg] = useState([]);
  const [infoIcon, setInfoIcon] = useState(false);
  const [manAnswer, setManAnswer] = useState(false);
  const [queText, setQueText] = useState(false);
  const [queRaw, setQueRaw] = useState(false);
  const [capText, setCapText] = useState(false);
  const [capRaw, setCapRaw] = useState(false);
  const [maxChar, setMaxChar] = useState("");
  const [infoTxt, setInfoTxt] = useState("");
  const [selectedVal, setSelectedVal] = useState("Select");
  const [selectedOpts, setSelectedOpts] = useState([]);
  const [addEditStatus, setAddEditStatus] = useState(null);
  const [formName, setFormName] = useState(
    localStorage.getItem("formName") ? localStorage.getItem("formName") : ""
  );
  const [editedFormName, setEditedFormName] = useState(
    localStorage.getItem("formName") ? localStorage.getItem("formName") : ""
  );
  const [editingFormName, setEditingFormName] = useState(false);
  const [lastDroppedComponent, setLastDroppedComponent] = useState(null);
  const [focusFormName, setFocusFormName] = useState(false);
  const [open, setOpen] = useState(true);
  const [objectType, setObjectType] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [createRadioButton] = useMutation(CREATE_RADIOBUTTON_MUTATION);
  const [createTextComponent] = useMutation(CREATE_TEXT_COMPONENT_MUTATION);
  const [createDropDownComponent] = useMutation(CREATE_DROPDOWN_MUTATION);
  const [createCheckboxComponent] = useMutation(CREATE_CHECKBOX_MUTATION);
  const [orderObjects, setOrderObjects] = useState([]);
  const [preorderObjects, setPreOrderObjects] = useState([]);
  const [orderStatus, setOrderStatus] = useState(false);

  const handleDrop = useCallback(
    (component) => setLastDroppedComponent(component),
    null
  );
  const handleDoneAddEditObjectToForm = (
    object,
    addEditStatus,
    infoIcon,
    manAnswer,
    queText,
    queRaw,
    capText,
    capRaw,
    maxChar = null,
    infoTxt,
    selectedVal = null,
    selectedOpts = null,
    mutiOptFields = []
  ) => {
    setLastDroppedComponent(null);
    setObjectType(object);
    setOpenEditDialog(false);
    setAddEditStatus(addEditStatus);
    setInfoIcon(infoIcon);
    setManAnswer(manAnswer);
    setQueText(queText);
    setQueRaw(queRaw);
    setCapText(capText);
    setCapRaw(capRaw);
    setMaxChar(maxChar);
    setInfoTxt(infoTxt);
    setSelectedVal(selectedVal);
    setSelectedOpts(selectedOpts);
    setMutiOptFields(mutiOptFields);
    if (object == "Radio Button") {
      saveRadioButton(queRaw, capRaw, manAnswer, infoTxt, selectedOpts);
    } else if (object == "Text") {
      saveTextComponent(infoTxt, manAnswer, queRaw, capRaw, maxChar);
    } else if (object == "Drop Down") {
      saveDropDownComponent(queRaw, capRaw, manAnswer, infoTxt, selectedOpts);
    } else if (object == "Checkbox") {
      saveCheckboxComponent(queRaw, capRaw, manAnswer, infoTxt, mutiOptFields);
    }
    setOrderObjects([
      {
        id: 1,
        order: 1, // It must be read from DB
        infoIcon,
        setManAnswer,
        setQueRaw,
        setCapRaw,
        maxChar,
        infoTxt,
        selectedVal,
        selectedOpts,
        mutiOptFields,
      },
      {
        id: 2,
        order: 2,
        infoIcon,
        setManAnswer,
        setQueRaw,
        setCapRaw,
        maxChar,
        infoTxt,
        selectedVal,
        selectedOpts,
        mutiOptFields,
      },
      {
        id: 3,
        order: 3,
        infoIcon,
        setManAnswer,
        setQueRaw,
        setCapRaw,
        maxChar,
        infoTxt,
        selectedVal,
        selectedOpts,
        mutiOptFields,
      },
    ]);
  };

  const saveTextComponent = async (
    infoTxt,
    manAnswer,
    queRaw,
    capRaw,
    maxChar
  ) => {
    if (!maxChar) maxChar = 100;

    if (formId) {
      const { data, error } = await createTextComponent({
        variables: {
          tooltip: infoTxt,
          mandatory: manAnswer,
          label: JSON.stringify(queRaw),
          caption: JSON.stringify(capRaw),
          maxLength: parseInt(maxChar),
          formId: parseInt(formId),
        },
      });

      if (error) {
        console.log("Error", error, data);
        //  setTimeout("");
      } else if (data && data.createTextComponent) {
        console.log("Success Saving Text Component");
        alert("New Text Question added successfully to " + formName);
      }
    } else {
      alert("Are you sure you have valid URL?");
    }
  };

  const saveRadioButton = async (
    queRaw,
    capRaw,
    manAnswer,
    infoTxt,
    selectedOpts
  ) => {
    const options = selectedOpts.map(({ title }) => title);
    var order = 1;
    const { data, error } = await createRadioButton({
      variables: {
        formId: parseInt(formId),
        infoTxt: infoTxt,
        manAnswer: manAnswer,
        queText: JSON.stringify(queRaw),
        capText: JSON.stringify(capRaw),
        order,
        options,
      },
    });

    if (error) {
      console.log("Error", error, data);
      //  setTimeout("");
    } else {
      console.log("Success");
    }
  };

  const saveDropDownComponent = async (
    queRaw,
    capRaw,
    manAnswer,
    infoTxt,
    selectedOpts
  ) => {
    if (formId) {
      const options = selectedOpts.map(({ title }) => title);
      var order = 1;
      const { data, error } = await createDropDownComponent({
        variables: {
          formId: parseInt(formId),
          tooltip: infoTxt,
          mandatory: manAnswer,
          label: JSON.stringify(queRaw),
          caption: JSON.stringify(capRaw),
          order: order,
          options: options,
        },
      });

      if (error) {
        console.log("Error", error, data);
        //  setTimeout("");
      } else {
        console.log("Success Saving Drop Down Component");
        alert(
          "New Drop Down Question was successfully added to '" +
            formName +
            "' form."
        );
      }
    } else {
      alert("Are you sure you have valid URL?");
    }
  };

  const saveCheckboxComponent = async (
    queRaw,
    capRaw,
    manAnswer,
    infoTxt,
    mutiOptFields
  ) => {
    if (formId) {
      let options = [];
      if (mutiOptFields.length > 0)
        options = mutiOptFields.map(({ textVal }) => textVal);
      console.log(options);
      var order = 1;
      const { data, error } = await createCheckboxComponent({
        variables: {
          formId: parseInt(formId),
          tooltip: infoTxt,
          mandatory: manAnswer,
          label: JSON.stringify(queRaw),
          caption: JSON.stringify(capRaw),
          order: order,
          options: options,
        },
      });

      if (error) {
        console.log("Error", error, data);
        //  setTimeout("");
      } else {
        console.log("Success Saving Checkbox Component");
        alert(
          "New Checkbox Question was successfully added to '" +
            formName +
            "' form."
        );
      }
    } else {
      alert("Are you sure you have valid URL?");
    }
  };

  const handleEditFormName = () => {
    setFormName(editedFormName);
    setEditingFormName(false);
    navigate(`${project_url}/forms/myforms/${editedFormName}`);
  };
  const moveOrderCard = useCallback((dragIndex, hoverIndex) => {
    setOrderObjects((prevCards) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      })
    );
  }, []);
  const renderOrderCard = useCallback(
    (object, index) => {
      return (
        <OrderObject
          key={object.id}
          index={index} // SP thinks it would be {object.order}
          id={object.id}
          moveCard={moveOrderCard}
        >
          <Box className={classes.objectBoxStyle}>
            <List
              key="01"
              sx={{
                paddingTop: 0,
                paddingBottom: 0,
                "& .MuiListItemButton-root": {
                  cursor: "move",
                },
                "& .MuiListItemButton-root:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <ListItemButton
                disableRipple
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingLeft: 10,
                  paddingTop: 5,
                  paddingBottom: 0,
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    paddingTop: 10,
                  }}
                >
                  {objectType === "Text" && (
                    <ShortTextIcon
                      style={{
                        marginRight: 10,
                        color: "#fff",
                        backgroundColor: "#0057B2",
                        borderRadius: 4,
                      }}
                    />
                  )}
                  {objectType === "Text Area" && (
                    <SubjectIcon
                      style={{
                        marginRight: 10,
                        color: "#fff",
                        backgroundColor: "#0057B2",
                        borderRadius: 4,
                      }}
                    />
                  )}
                  {objectType === "Content" && (
                    <TocIcon
                      style={{
                        marginRight: 10,
                        color: "#fff",
                        backgroundColor: "#0057B2",
                        borderRadius: 4,
                      }}
                    />
                  )}
                  {objectType === "Drop Down" && (
                    <img
                      style={{
                        marginRight: 10,
                        borderRadius: 4,
                        height: 24,
                      }}
                      src={dropdownImg}
                    />
                  )}
                  {objectType === "Radio Button" && (
                    <RadioButtonCheckedIcon
                      style={{
                        marginRight: 10,
                        color: "#fff",
                        backgroundColor: "#0057B2",
                        borderRadius: 4,
                      }}
                    />
                  )}

                  {objectType === "Checkbox" && (
                    <CheckBoxIcon
                      style={{
                        marginRight: 10,
                        color: "#fff",
                        backgroundColor: "#0057B2",
                        borderRadius: 4,
                        marginTop: mutiOptFields.length == 0 && 8,
                      }}
                    />
                  )}

                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    {infoIcon && infoTxt ? (
                      <StyledTooltip title={infoTxt}>
                        <img
                          style={{
                            paddingBottom: 3,
                            height: 24,
                            paddingTop:
                              objectType === "Checkbox" &&
                              mutiOptFields.length == 0 &&
                              8,
                          }}
                          src={infoImg}
                        />
                      </StyledTooltip>
                    ) : infoIcon && !infoTxt ? (
                      <img
                        style={{
                          paddingBottom: 3,
                          height: 24,
                          paddingTop:
                            objectType === "Checkbox" &&
                            mutiOptFields.length == 0 &&
                            8,
                        }}
                        src={infoImg}
                      />
                    ) : (
                      ""
                    )}
                  </Box>
                  <Typography variant="h3">
                    <div
                      style={{
                        wordBreak: "break-word",
                        paddingLeft: 5,
                      }}
                    >
                      {queText ? object.id : ""}{" "}
                      {objectType === "Checkbox" &&
                        mutiOptFields.length == 0 &&
                        manAnswer && (
                          <>
                            <span
                              style={{
                                color: "#E53935",
                                verticalAlign: "sub",
                              }}
                            >
                              {" "}
                              *
                            </span>
                            <Checkbox />
                          </>
                        )}
                      {objectType === "Checkbox" &&
                        mutiOptFields.length == 0 &&
                        !manAnswer && <Checkbox />}
                      {queText ? queText : ""}
                    </div>
                  </Typography>
                </Box>
              </ListItemButton>
            </List>
          </Box>
        </OrderObject>
      );
    },
    [queText, open]
  );
  return (
    <div className={classes.cardContainer}>
      <Header breadcrumbName={formName ? formName : ""} />
      <div className={classes.container}>
        <DndProvider backend={HTML5Backend}>
          <Grid container spacing={1}>
            <Grid item lg={2} md={2} xl={2} xs={12}>
              <Card style={{ height: 936 }}>
                <Box mb={1}>
                  <Box
                    mt={2}
                    mb={1}
                    ml={1}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "start",
                    }}
                  >
                    <IconButton>
                      <img
                        style={{ cursor: "pointer" }}
                        src={backImg}
                        height={24}
                        onClick={() => navigate(`${project_url}/forms`)}
                      />
                    </IconButton>
                    <Typography
                      variant="h1"
                      style={{
                        fontSize: 20,
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: 10,
                      }}
                    >
                      Back
                    </Typography>
                  </Box>
                  <Divider />
                  <Box className={classes.dragIconBoxStyle}>
                    <ComponentDragBox
                      component={Components.TEXT_FIELD}
                      text="Text"
                      icon="ShortTextIcon"
                    />
                  </Box>
                  <Box className={classes.dragIconBoxStyle}>
                    <ComponentDragBox
                      component={Components.TEXT_AREA_FIELD}
                      text="Text Area"
                      icon="SubjectIcon"
                    />
                  </Box>
                  <Box className={classes.dragIconBoxStyle}>
                    <ComponentDragBox
                      component={Components.CONTENT_FIELD}
                      text="Content"
                      icon="TocIcon"
                    />
                  </Box>
                  <Box className={classes.dragIconBoxStyle}>
                    <ComponentDragBox
                      component={Components.DROP_DOWN_FIELD}
                      text="Drop Down"
                      icon="DropDownIcon"
                    />
                  </Box>
                  <Box className={classes.dragIconBoxStyle}>
                    <ComponentDragBox
                      component={Components.RADIO_BUTTON}
                      text="Radio Button"
                      icon="RadioButtonCheckedIcon"
                    />
                  </Box>
                  <Box className={classes.dragIconBoxStyle}>
                    <ComponentDragBox
                      component={Components.CHECKBOX_FIELD}
                      text="Checkbox"
                      icon="CheckBoxIcon"
                    />
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item lg={10} md={10} xl={10} xs={12}>
              <Card style={{ height: 936 }}>
                <Box
                  mt={1.5}
                  mb={1.5}
                  ml={3}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  {!editingFormName ? (
                    <Box className={classes.formNameTextBoxStyle}>
                      <Typography
                        variant="h1"
                        className={classes.formNameTextStyle}
                      >
                        {formName}
                      </Typography>
                      <img
                        style={{
                          paddingLeft: 15,
                          cursor: "pointer",
                          height: 24,
                        }}
                        src={penImg}
                        onClick={() => {
                          setEditingFormName(true);
                          setFocusFormName(true);
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "start",
                        width: 400,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        id="outlined-multiline-static"
                        variant="outlined"
                        value={editedFormName}
                        inputRef={(input) => {
                          if (focusFormName && input != null) {
                            input.focus();
                          }
                        }}
                        className={classes.formNameFieldStyle}
                        onChange={(e) => {
                          setEditedFormName(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          e.key === "Enter" && handleEditFormName();
                        }}
                      />

                      <Box
                        className={classes.iconStyle}
                        onClick={handleEditFormName}
                      >
                        <CheckIcon sx={{ color: "gray", cursor: "pointer" }} />
                      </Box>

                      <Box
                        className={classes.iconStyle}
                        style={{ marginLeft: 7 }}
                        onClick={() => {
                          setEditingFormName(false);
                          setEditedFormName(formName);
                        }}
                      >
                        <CloseIcon sx={{ color: "gray", cursor: "pointer" }} />
                      </Box>
                    </Box>
                  )}
                  <Box
                    mr={3}
                    mt={1.5}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <IconButton
                      style={{ cursor: "pointer", paddingRight: 8 }}
                      onClick={() => {
                        setOrderStatus(true);
                        setPreOrderObjects(orderObjects);
                      }}
                      disabled={orderStatus || orderObjects.length === 0}
                    >
                      <SwapVertOutlinedIcon />
                    </IconButton>
                    <VisibilityIcon style={{ cursor: "pointer" }} />
                  </Box>
                </Box>
                <Divider />
                {addEditStatus && !orderStatus && (
                  <Box className={classes.objectBoxStyle}>
                    <List
                      sx={{
                        paddingTop: 0,
                        paddingBottom: 0,
                        "& .MuiListItemButton-root:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <ListItemButton
                        disableRipple
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          paddingLeft: 10,
                          paddingTop: 5,
                          paddingBottom: 0,
                        }}
                        onClick={() => setOpenEditDialog(!openEditDialog)}
                      >
                        <Box
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            paddingTop: 10,
                          }}
                        >
                          {objectType === "Text" && (
                            <ShortTextIcon
                              style={{
                                marginRight: 10,
                                color: "#fff",
                                backgroundColor: "#0057B2",
                                borderRadius: 4,
                              }}
                            />
                          )}
                          {objectType === "Text Area" && (
                            <SubjectIcon
                              style={{
                                marginRight: 10,
                                color: "#fff",
                                backgroundColor: "#0057B2",
                                borderRadius: 4,
                              }}
                            />
                          )}
                          {objectType === "Content" && (
                            <TocIcon
                              style={{
                                marginRight: 10,
                                color: "#fff",
                                backgroundColor: "#0057B2",
                                borderRadius: 4,
                              }}
                            />
                          )}
                          {objectType === "Drop Down" && (
                            <img
                              style={{
                                marginRight: 10,
                                borderRadius: 4,
                                height: 24,
                              }}
                              src={dropdownImg}
                            />
                          )}
                          {objectType === "Radio Button" && (
                            <RadioButtonCheckedIcon
                              style={{
                                marginRight: 10,
                                color: "#fff",
                                backgroundColor: "#0057B2",
                                borderRadius: 4,
                              }}
                            />
                          )}

                          {objectType === "Checkbox" && (
                            <CheckBoxIcon
                              style={{
                                marginRight: 10,
                                color: "#fff",
                                backgroundColor: "#0057B2",
                                borderRadius: 4,
                                marginTop: mutiOptFields.length == 0 && 8,
                              }}
                            />
                          )}

                          <Box
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            {infoIcon && infoTxt ? (
                              <StyledTooltip title={infoTxt}>
                                <img
                                  style={{
                                    paddingBottom: 3,
                                    height: 24,
                                    paddingTop:
                                      objectType === "Checkbox" &&
                                      mutiOptFields.length == 0 &&
                                      8,
                                  }}
                                  src={infoImg}
                                />
                              </StyledTooltip>
                            ) : infoIcon && !infoTxt ? (
                              <img
                                style={{
                                  paddingBottom: 3,
                                  height: 24,
                                  paddingTop:
                                    objectType === "Checkbox" &&
                                    mutiOptFields.length == 0 &&
                                    8,
                                }}
                                src={infoImg}
                              />
                            ) : (
                              ""
                            )}
                          </Box>
                          <Typography variant="h3">
                            <div
                              style={{
                                wordBreak: "break-word",
                                paddingLeft: 5,
                              }}
                            >
                              {queText
                                ? objectType === "Text"
                                  ? "1. "
                                  : objectType === "Text Area"
                                  ? "2. "
                                  : objectType === "Radio Button"
                                  ? "1. "
                                  : ""
                                : ""}

                              {objectType === "Checkbox" &&
                                mutiOptFields.length == 0 &&
                                manAnswer && (
                                  <>
                                    <span
                                      style={{
                                        color: "#E53935",
                                        verticalAlign: "sub",
                                      }}
                                    >
                                      {" "}
                                      *
                                    </span>
                                    <Checkbox />
                                  </>
                                )}
                              {objectType === "Checkbox" &&
                                mutiOptFields.length == 0 &&
                                !manAnswer && <Checkbox />}

                              {queText ? queText : ""}
                            </div>
                          </Typography>
                        </Box>

                        <Box
                          pl={1}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <StyledTooltip title="Delete this question">
                            <img
                              src={trashImg}
                              height={24}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAlertMsg({
                                  title: "Delete This Question",
                                  type: "deleteConfirmation",
                                  msg:
                                    "Are you sure you want to remove this question?",
                                });
                              }}
                            />
                          </StyledTooltip>
                          <IconButton
                            onClick={(e) => {
                              setOpen(!open);
                              e.stopPropagation();
                            }}
                            aria-label="expand"
                            size="small"
                          >
                            {open ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </Box>
                      </ListItemButton>

                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <CardContent
                          style={{
                            paddingTop: 20,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            marginLeft: 30,
                          }}
                        >
                          {capRaw && capRaw.blocks[0].text && (
                            <Typography
                              variant="h3"
                              style={{
                                fontSize: 14,
                                display: "flex",
                                alignItems: "center",
                                paddingBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "left",
                                  wordBreak: "break-word",
                                  paddingLeft: 5,
                                }}
                              >
                                {capText}
                              </div>
                            </Typography>
                          )}
                          {(objectType === "Text" ||
                            objectType === "Text Area") && (
                            <>
                              <Box
                                pb={0.25}
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                <Typography
                                  variant="h3"
                                  style={{
                                    fontSize: 14,
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: 5,
                                  }}
                                >
                                  Answer
                                </Typography>
                                {manAnswer && (
                                  <StyledTooltip title="required field">
                                    <Typography
                                      variant="h3"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        color: "#E53935",
                                        paddingLeft: 5,
                                        paddingRight: 5,
                                      }}
                                    >
                                      *
                                    </Typography>
                                  </StyledTooltip>
                                )}
                              </Box>

                              <TextField
                                id="outlined-multiline-static"
                                variant="outlined"
                                className={classes.textFieldStyle}
                              />
                              <Typography
                                variant="h6"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  paddingRight: 5,
                                  paddingTop: 5,
                                  color: "#00000099",
                                }}
                              >
                                {maxChar
                                  ? maxChar +
                                    " of " +
                                    maxChar +
                                    " Character(s) left"
                                  : ""}
                              </Typography>
                            </>
                          )}
                          {objectType === "Drop Down" && (
                            <ThemeProvider theme={formLabelsTheme}>
                              <form noValidate autoComplete="off">
                                <Autocomplete
                                  key={selectedVal}
                                  options={selectedOpts}
                                  disableClearable
                                  fullWidth
                                  getOptionLabel={(option) => option.title}
                                  renderInput={(params) => (
                                    <TextField
                                      required={manAnswer}
                                      fullWidth
                                      label={
                                        selectedVal === "Select"
                                          ? "Select"
                                          : selectedVal === "Use State"
                                          ? "Select State"
                                          : "Select Country"
                                      }
                                      margin="normal"
                                      name="selectOptions"
                                      id="selectOptions"
                                      variant="outlined"
                                      {...params}
                                    />
                                  )}
                                />
                              </form>
                            </ThemeProvider>
                          )}
                          {objectType === "Radio Button" && (
                            <>
                              {manAnswer && (
                                <Box
                                  pb={0.25}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                  }}
                                >
                                  <Typography
                                    variant="h3"
                                    style={{
                                      fontSize: 14,
                                      display: "flex",
                                      alignItems: "center",
                                      paddingLeft: 5,
                                    }}
                                  >
                                    Required
                                  </Typography>
                                  <StyledTooltip title="required field">
                                    <Typography
                                      variant="h3"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        color: "#E53935",
                                        paddingLeft: 5,
                                        paddingRight: 5,
                                      }}
                                    >
                                      *
                                    </Typography>
                                  </StyledTooltip>
                                </Box>
                              )}
                              <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                defaultValue="female"
                                name="radio-buttons-group"
                              >
                                {selectedOpts &&
                                  selectedOpts.map((option, index) => (
                                    <FormControlLabel
                                      value={option.title}
                                      control={<Radio />}
                                      label={option.title}
                                      className={classes.radioLabelStyle}
                                    />
                                  ))}
                              </RadioGroup>
                            </>
                          )}

                          {objectType === "Checkbox" && (
                            <>
                              {mutiOptFields.length > 0 && manAnswer && (
                                <Box
                                  pb={0.25}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                  }}
                                >
                                  <Typography
                                    variant="h3"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      paddingLeft: 5,
                                      paddingRight: 5,
                                      fontSize: 14,
                                    }}
                                  >
                                    <div>
                                      Required{" "}
                                      <span style={{ color: "#E53935" }}>
                                        {" "}
                                        *
                                      </span>
                                    </div>
                                  </Typography>
                                </Box>
                              )}
                              <Box
                                style={{
                                  // paddingTop: 5,
                                  display: "flex",
                                  flexDirection: "column",
                                  // justifyContent: "flex-start",
                                  // marginLeft: 30,
                                }}
                              >
                                {mutiOptFields.map((element, index) => (
                                  <FormControlLabel
                                    control={<Checkbox />}
                                    label={element.textVal}
                                  />
                                ))}
                              </Box>
                            </>
                          )}
                        </CardContent>
                      </Collapse>
                    </List>
                  </Box>
                )}
                {orderStatus && orderObjects.length > 0 && (
                  <>{orderObjects.map((object, i) => renderOrderCard(object, i))}</>
                )}
                {!orderStatus && (
                  <>
                    <Box mt={3} ml={2} mr={2}>
                      <FormDropBox
                        lastDroppedComponent={lastDroppedComponent}
                        onDrop={handleDrop}
                        passComOptions={(
                          object,
                          addEditStatus,
                          infoIcon,
                          manAnswer,
                          queText,
                          queRaw,
                          capText,
                          capRaw,
                          maxChar,
                          infoTxt,
                          selectedVal,
                          selectedOpts,
                          mutiOptFields
                        ) =>
                          handleDoneAddEditObjectToForm(
                            object,
                            addEditStatus,
                            infoIcon,
                            manAnswer,
                            queText,
                            queRaw,
                            capText,
                            capRaw,
                            maxChar,
                            infoTxt,
                            selectedVal,
                            selectedOpts,
                            mutiOptFields
                          )
                        }
                        closeAddDialog={() => setLastDroppedComponent(null)}
                      />
                    </Box>
                    <Box
                      mt={3}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="h5"
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Drag and drop your object into this box.
                      </Typography>
                      <img
                        style={{
                          paddingLeft: 5,
                          paddingBottom: 5,
                          height: 24,
                        }}
                        src={arrowupImg}
                      />
                    </Box>
                  </>
                )}
                {orderStatus && (
                  <Box mt={2} ml={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.buttonStyle}
                      style={{ marginRight: 2 }}
                      onClick={() => setOrderStatus(false)}
                    >
                      Save Order
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      className={classes.outlinedButtonStyle}
                      onClick={() => {
                        setOrderObjects(preorderObjects);
                        setOrderStatus(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Card>
            </Grid>
            {alertMsg.msg && (
              <AlertDialog
                title={alertMsg.title}
                type={alertMsg.type}
                contentMsg={alertMsg.msg}
                closeDialog={() => setAlertMsg([])}
                deleteAction={() => {
                  setAlertMsg([]);
                  setAddEditStatus(null);
                }}
              />
            )}
            {openEditDialog && objectType === "Text" && (
              <EditTextToFormDialog
                closeDialog={() => setOpenEditDialog(false)}
                infoIcon={infoIcon}
                manAnswer={manAnswer}
                queText={queText}
                queRaw={queRaw}
                capText={capText}
                capRaw={capRaw}
                maxChar={maxChar}
                infoTxt={infoTxt}
                handleDone={(
                  object,
                  addEditStatus,
                  infoIcon,
                  manAnswer,
                  queText,
                  queRaw,
                  capText,
                  capRaw,
                  maxChar,
                  infoTxt
                ) =>
                  handleDoneAddEditObjectToForm(
                    object,
                    addEditStatus,
                    infoIcon,
                    manAnswer,
                    queText,
                    queRaw,
                    capText,
                    capRaw,
                    maxChar,
                    infoTxt
                  )
                }
              />
            )}
            {openEditDialog && objectType === "Text Area" && (
              <EditTextAreaToFormDialog
                closeDialog={() => setOpenEditDialog(false)}
                infoIcon={infoIcon}
                manAnswer={manAnswer}
                queText={queText}
                queRaw={queRaw}
                capText={capText}
                capRaw={capRaw}
                maxChar={maxChar}
                infoTxt={infoTxt}
                handleDone={(
                  object,
                  addEditStatus,
                  infoIcon,
                  manAnswer,
                  queText,
                  queRaw,
                  capText,
                  capRaw,
                  maxChar,
                  infoTxt
                ) =>
                  handleDoneAddEditObjectToForm(
                    object,
                    addEditStatus,
                    infoIcon,
                    manAnswer,
                    queText,
                    queRaw,
                    capText,
                    capRaw,
                    maxChar,
                    infoTxt
                  )
                }
              />
            )}
            {openEditDialog && objectType === "Content" && (
              <EditContentToFormDialog
                closeDialog={() => setOpenEditDialog(false)}
                queText={queText}
                queRaw={queRaw}
                capText={capText}
                capRaw={capRaw}
                handleDone={(
                  object,
                  addEditStatus,
                  infoIcon,
                  manAnswer,
                  queText,
                  queRaw,
                  capText,
                  capRaw,
                  maxChar,
                  infoTxt
                ) =>
                  handleDoneAddEditObjectToForm(
                    object,
                    addEditStatus,
                    infoIcon,
                    manAnswer,
                    queText,
                    queRaw,
                    capText,
                    capRaw,
                    maxChar,
                    infoTxt
                  )
                }
              />
            )}
            {openEditDialog && objectType === "Drop Down" && (
              <EditDropDownToFormDialog
                closeDialog={() => setOpenEditDialog(false)}
                infoIcon={infoIcon}
                manAnswer={manAnswer}
                queText={queText}
                queRaw={queRaw}
                capText={capText}
                capRaw={capRaw}
                maxChar={maxChar}
                infoTxt={infoTxt}
                selectedVal={selectedVal}
                selectedOpts={selectedOpts}
                handleDone={(
                  object,
                  addEditStatus,
                  infoIcon,
                  manAnswer,
                  queText,
                  queRaw,
                  capText,
                  capRaw,
                  maxChar,
                  infoTxt,
                  selectedVal,
                  selectedOpts
                ) =>
                  handleDoneAddEditObjectToForm(
                    object,
                    addEditStatus,
                    infoIcon,
                    manAnswer,
                    queText,
                    queRaw,
                    capText,
                    capRaw,
                    maxChar,
                    infoTxt,
                    selectedVal,
                    selectedOpts
                  )
                }
              />
            )}
            {openEditDialog && objectType === "Radio Button" && (
              <EditRadioButtonToFormDialog
                closeDialog={() => setOpenEditDialog(false)}
                infoIcon={infoIcon}
                manAnswer={manAnswer}
                queText={queText}
                queRaw={queRaw}
                capText={capText}
                capRaw={capRaw}
                maxChar={maxChar}
                infoTxt={infoTxt}
                selectedVal={selectedVal}
                selectedOpts={selectedOpts}
                handleDone={(
                  object,
                  addEditStatus,
                  infoIcon,
                  manAnswer,
                  queText,
                  queRaw,
                  capText,
                  capRaw,
                  maxChar,
                  infoTxt,
                  selectedVal,
                  selectedOpts
                ) =>
                  handleDoneAddEditObjectToForm(
                    object,
                    addEditStatus,
                    infoIcon,
                    manAnswer,
                    queText,
                    queRaw,
                    capText,
                    capRaw,
                    null,
                    infoTxt,
                    null,
                    selectedOpts
                  )
                }
              />
            )}

            {openEditDialog && objectType === "Checkbox" && (
              <EditCheckboxToFormDialog
                closeDialog={() => setOpenEditDialog(false)}
                infoIcon={infoIcon}
                manAnswer={manAnswer}
                queText={queText}
                queRaw={queRaw}
                capText={capText}
                capRaw={capRaw}
                maxChar={maxChar}
                infoTxt={infoTxt}
                selectedVal={selectedVal}
                selectedOpts={selectedOpts}
                mutiOptFields={mutiOptFields}
                handleDone={(
                  object,
                  addEditStatus,
                  infoIcon,
                  manAnswer,
                  queText,
                  queRaw,
                  capText,
                  capRaw,
                  maxChar,
                  infoTxt,
                  selectedVal,
                  selectedOpts,
                  mutiOptFields
                ) =>
                  handleDoneAddEditObjectToForm(
                    object,
                    addEditStatus,
                    infoIcon,
                    manAnswer,
                    queText,
                    queRaw,
                    capText,
                    capRaw,
                    null,
                    infoTxt,
                    null,
                    null,
                    mutiOptFields
                  )
                }
              />
            )}
          </Grid>
        </DndProvider>
      </div>
    </div>
  );
}

export default OrderFormDialog;
